/**
 * TradingChart — TradingView Lightweight Charts v4 candlestick chart
 * with volume histogram and trade entry/exit markers.
 *
 * Single-symbol mode  → candlestick + volume + trade markers
 * Multi-symbol mode   → normalized % return line chart (one line per symbol)
 *
 * Props:
 *   candles         — OHLCV data (single-symbol mode)
 *   markers         — trade markers (single-symbol mode)
 *   multiSymbolData — [{symbol, candles, color}] for multi-symbol mode
 *   height          — chart height in px (default 420)
 *   isLoading
 */

import React, { useEffect, useRef, memo } from 'react';
import {
  createChart,
  ColorType,
  CrosshairMode,
  SeriesMarker,
  Time,
  UTCTimestamp,
} from 'lightweight-charts';
import type { IChartApi, ISeriesApi } from 'lightweight-charts';
import { OhlcvCandle, TradeMarker } from '../types/trading';

export interface AgentLegendEntry {
  id: string;
  name: string;
  color: string;
}

export interface MultiSymbolEntry {
  symbol: string;
  candles: OhlcvCandle[];
  color: string;
  markers?: TradeMarker[];
}

interface MarketStatus {
  open: boolean;
  label: string;
  badge: string;
}

function getCryptoMarketStatus(): MarketStatus {
  return { open: true, label: 'Crypto — 24/7', badge: '24H' };
}

function getEquityMarketStatus(): MarketStatus {
  const now = new Date();
  const etStr = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const etDate = new Date(etStr);
  const day = etDate.getDay();

  if (day === 0 || day === 6) {
    return { open: false, label: 'NYSE Closed — Weekend', badge: 'Closed' };
  }

  const h = etDate.getHours();
  const m = etDate.getMinutes();
  const mins = h * 60 + m;

  if (mins >= 570 && mins < 960) {
    return { open: true, label: 'NYSE Open  9:30–16:00 ET', badge: 'Open' };
  } else if (mins >= 240 && mins < 570) {
    return { open: false, label: 'NYSE Pre-Market — opens 9:30 ET', badge: 'Pre-Mkt' };
  } else {
    return { open: false, label: 'NYSE Closed — opens 9:30 ET', badge: 'Closed' };
  }
}

interface TradingChartProps {
  candles: OhlcvCandle[];
  markers?: TradeMarker[];
  height?: number;
  isLoading?: boolean;
  symbol?: string;
  timeframe?: string;
  currentPrice?: number;
  agentColorMap?: Record<string, string>;
  agentLegend?: AgentLegendEntry[];
  marketType?: 'crypto' | 'equities';
  /** Multi-symbol mode: when provided with 2+ entries, switches to normalized % return lines */
  multiSymbolData?: MultiSymbolEntry[];
}

const COLORS = {
  background:  '#0f172a',
  background2: '#1e293b',
  grid:        '#1e293b',
  border:      '#334155',
  text:        '#94a3b8',
  textLight:   '#e2e8f0',
  candleUp:    '#22c55e',
  candleDown:  '#ef4444',
  volume:      '#334155',
  buyMarker:   '#22c55e',
  sellMarker:  '#ef4444',
};

const TradingChart: React.FC<TradingChartProps> = ({
  candles,
  markers = [],
  height = 420,
  isLoading = false,
  symbol = '',
  timeframe = '',
  currentPrice,
  agentColorMap,
  agentLegend,
  marketType = 'crypto',
  multiSymbolData,
}) => {
  const isMultiMode = (multiSymbolData?.length ?? 0) > 1;
  const marketStatus = marketType === 'crypto' ? getCryptoMarketStatus() : getEquityMarketStatus();

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);

  // Single-symbol series refs
  const candleRef  = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volumeRef  = useRef<ISeriesApi<'Histogram'> | null>(null);

  // Multi-symbol series ref: symbol → line series
  const lineMapRef = useRef<Map<string, ISeriesApi<'Line'>>>(new Map());

  // Keep timeframe accessible inside the formatter closure
  const timeframeRef = useRef(timeframe);
  useEffect(() => { timeframeRef.current = timeframe; }, [timeframe]);

  // -------------------------------------------------------------------------
  // Initialize chart shell once (no series added here)
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: COLORS.background },
        textColor: COLORS.text,
        fontFamily: "'Inter', 'system-ui', sans-serif",
        fontSize: 11,
      },
      grid: {
        vertLines: { color: COLORS.grid, style: 1 },
        horzLines: { color: COLORS.grid, style: 1 },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#475569', width: 1, style: 1, labelBackgroundColor: '#334155' },
        horzLine: { color: '#475569', width: 1, style: 1, labelBackgroundColor: '#334155' },
      },
      rightPriceScale: {
        borderColor: COLORS.border,
        textColor: COLORS.text,
        scaleMargins: { top: 0.1, bottom: 0.25 },
      },
      timeScale: {
        borderColor: COLORS.border,
        timeVisible: true,
        secondsVisible: false,
        rightOffset: 5,
        // Axis tick labels — must also be Eastern, otherwise lightweight-charts
        // renders them in UTC (which is 4-5 hours ahead of ET)
        tickMarkFormatter: (epochSeconds: number, markType: number) => {
          const tf = timeframeRef.current;
          const isDaily = tf === '1d' || tf === '1w';
          const date = new Date(epochSeconds * 1000);
          // markType 0 = year, 1 = month, 2 = day of month, 3 = time
          if (markType === 0) {
            return date.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
          }
          if (markType === 1) {
            return date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'short' });
          }
          if (markType === 2 || isDaily) {
            return date.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'numeric', day: 'numeric' });
          }
          // markType 3 — intraday time ticks
          return date.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true });
        },
      },
      localization: {
        // All timestamps rendered in US Eastern time
        timeFormatter: (epochSeconds: number) => {
          const tf = timeframeRef.current;
          const isDaily = tf === '1d' || tf === '1w';
          const date = new Date(epochSeconds * 1000);
          if (isDaily) {
            return date.toLocaleDateString('en-US', {
              timeZone: 'America/New_York',
              month: 'short', day: 'numeric', year: '2-digit',
            });
          }
          return date.toLocaleString('en-US', {
            timeZone: 'America/New_York',
            month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
            hour12: true,
          });
        },
      },
      width: containerRef.current.clientWidth,
      height,
    });

    chartRef.current = chart;

    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth });
      }
    });
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      volumeRef.current = null;
      lineMapRef.current.clear();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Manage series and update data whenever mode or data changes
  // -------------------------------------------------------------------------
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    if (isMultiMode && multiSymbolData) {
      // ── Multi-symbol mode ─────────────────────────────────────────────────
      // Remove single-symbol series if they exist
      if (candleRef.current) { chart.removeSeries(candleRef.current); candleRef.current = null; }
      if (volumeRef.current) { chart.removeSeries(volumeRef.current); volumeRef.current = null; }

      // Remove line series whose symbol is no longer in the list
      const newSymSet = new Set(multiSymbolData.map(d => d.symbol));
      for (const sym of [...lineMapRef.current.keys()]) {
        if (!newSymSet.has(sym)) {
          chart.removeSeries(lineMapRef.current.get(sym)!);
          lineMapRef.current.delete(sym);
        }
      }

      // Scale for % return (no volume padding)
      chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.08, bottom: 0.08 } });

      // Add/update series
      for (const { symbol: sym, candles: symCandles, color } of multiSymbolData) {
        if (!lineMapRef.current.has(sym)) {
          const ls = chart.addLineSeries({
            color,
            lineWidth: 2,
            lastValueVisible: true,
            priceLineVisible: false,
            crosshairMarkerVisible: true,
            crosshairMarkerRadius: 4,
            priceFormat: {
              type: 'custom' as const,
              minMove: 0.01,
              formatter: (p: number) => `${p >= 0 ? '+' : ''}${p.toFixed(2)}%`,
            },
          });
          lineMapRef.current.set(sym, ls);
        }

        const ls = lineMapRef.current.get(sym)!;

        if (symCandles.length === 0) {
          ls.setData([]);
          continue;
        }

        const base = symCandles[0].close;
        if (base === 0) { ls.setData([]); continue; }

        ls.setData(symCandles.map(c => ({
          time: c.time as UTCTimestamp,
          value: ((c.close - base) / base) * 100,
        })));
      }

      chart.timeScale().fitContent();

    } else {
      // ── Single-symbol mode ────────────────────────────────────────────────
      // Remove any lingering multi-symbol line series
      for (const series of lineMapRef.current.values()) { chart.removeSeries(series); }
      lineMapRef.current.clear();

      // Create candlestick + volume series if not yet present
      if (!candleRef.current) {
        const cs = chart.addCandlestickSeries({
          upColor:         COLORS.candleUp,
          downColor:       COLORS.candleDown,
          borderUpColor:   COLORS.candleUp,
          borderDownColor: COLORS.candleDown,
          wickUpColor:     COLORS.candleUp,
          wickDownColor:   COLORS.candleDown,
        });
        const vs = chart.addHistogramSeries({
          color: COLORS.volume,
          priceFormat: { type: 'volume' },
          priceScaleId: 'volume',
        });
        chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
        chart.priceScale('right').applyOptions({ scaleMargins: { top: 0.1, bottom: 0.25 } });
        candleRef.current = cs;
        volumeRef.current = vs;
      }

      if (candles.length === 0) {
        candleRef.current.setData([]);
        volumeRef.current?.setData([]);
        return;
      }

      candleRef.current.setData(candles.map(c => ({
        time:  c.time as UTCTimestamp,
        open:  c.open,
        high:  c.high,
        low:   c.low,
        close: c.close,
      })));

      volumeRef.current?.setData(candles.map(c => ({
        time:  c.time as UTCTimestamp,
        value: c.volume,
        color: c.close >= c.open ? COLORS.candleUp + '40' : COLORS.candleDown + '40',
      })));

      chart.timeScale().fitContent();
    }
  }, [candles, multiSymbolData, isMultiMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Markers — single-symbol candlestick mode
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!candleRef.current || isMultiMode) return;

    const seriesMarkers: SeriesMarker<Time>[] = markers.map(m => {
      const agentColor = agentColorMap?.[m.agent_id];
      const color = agentColor ?? (m.side === 'buy' ? COLORS.buyMarker : COLORS.sellMarker);
      return {
        time: m.time as UTCTimestamp,
        position: m.side === 'buy' ? 'belowBar' : 'aboveBar',
        color,
        shape: m.side === 'buy' ? 'arrowUp' : 'arrowDown',
        text: m.side === 'buy'
          ? `${m.agent_name}${m.is_paper ? ' [P]' : ''}`
          : `${m.agent_name}${m.pnl != null ? ` $${m.pnl >= 0 ? '+' : ''}${m.pnl.toFixed(2)}` : ''}${m.is_paper ? ' [P]' : ''}`,
        size: 1,
      };
    });

    seriesMarkers.sort((a, b) => (a.time as number) - (b.time as number));
    candleRef.current.setMarkers(seriesMarkers);
  }, [markers, agentColorMap, isMultiMode]);

  // -------------------------------------------------------------------------
  // Markers — multi-symbol line mode
  // Each line series gets markers for its own symbol only.
  // Marker Y position is mapped to the normalized % value at that candle's close.
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isMultiMode || !multiSymbolData) return;

    for (const { symbol: sym, candles: symCandles, markers: symMarkers } of multiSymbolData) {
      const ls = lineMapRef.current.get(sym);
      if (!ls) continue;

      if (!symMarkers || symMarkers.length === 0) {
        ls.setMarkers([]);
        continue;
      }

      const base = symCandles[0]?.close ?? 0;

      const seriesMarkers: SeriesMarker<Time>[] = symMarkers.map(m => {
        const agentColor = agentColorMap?.[m.agent_id];
        const color = agentColor ?? (m.side === 'buy' ? COLORS.buyMarker : COLORS.sellMarker);
        return {
          time: m.time as UTCTimestamp,
          position: m.side === 'buy' ? 'belowBar' : 'aboveBar',
          color,
          shape: m.side === 'buy' ? 'arrowUp' : 'arrowDown',
          text: m.side === 'buy'
            ? `${m.agent_name}${m.is_paper ? ' [P]' : ''}`
            : `${m.agent_name}${m.pnl != null ? ` $${m.pnl >= 0 ? '+' : ''}${m.pnl.toFixed(2)}` : ''}${m.is_paper ? ' [P]' : ''}`,
          size: 1,
        };
      });

      seriesMarkers.sort((a, b) => (a.time as number) - (b.time as number));
      ls.setMarkers(seriesMarkers);

      void base; // base used indirectly to ensure candle data is present
    }
  }, [multiSymbolData, agentColorMap, isMultiMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Live last-candle update — single-symbol only
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!candleRef.current || !currentPrice || candles.length === 0 || isMultiMode) return;
    const last = candles[candles.length - 1];
    candleRef.current.update({
      time:  last.time as UTCTimestamp,
      open:  last.open,
      high:  Math.max(last.high, currentPrice),
      low:   Math.min(last.low,  currentPrice),
      close: currentPrice,
    });
    volumeRef.current?.update({
      time:  last.time as UTCTimestamp,
      value: last.volume,
      color: currentPrice >= last.open ? COLORS.candleUp + '40' : COLORS.candleDown + '40',
    });
  }, [currentPrice]); // eslint-disable-line react-hooks/exhaustive-deps

  // -------------------------------------------------------------------------
  // Empty state check
  // -------------------------------------------------------------------------
  const isEmpty = isMultiMode
    ? !multiSymbolData || multiSymbolData.every(d => !d.candles || d.candles.length === 0)
    : !candles || candles.length === 0;

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-700 bg-slate-900">

      {/* ── Multi-symbol header ── */}
      {isMultiMode && multiSymbolData && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-700 bg-slate-900/80 flex-wrap">
          {multiSymbolData.map(({ symbol: sym, color }) => (
            <span key={sym} className="flex items-center gap-1.5 text-sm font-semibold" style={{ color }}>
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
              {sym}
            </span>
          ))}
          {timeframe && (
            <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded">{timeframe}</span>
          )}
          {/* Agent legend in multi mode */}
          {agentLegend && agentLegend.length > 0 && (
            <div className="flex items-center gap-2 ml-2 border-l border-slate-700 pl-3">
              {agentLegend.map(a => (
                <span key={a.id} className="flex items-center gap-1.5 text-xs whitespace-nowrap" style={{ color: a.color }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: a.color }} />
                  {a.name}
                </span>
              ))}
              <span className="text-slate-500 text-xs">↑buy ↓sell</span>
            </div>
          )}
          <span className="ml-auto text-xs text-slate-500 italic whitespace-nowrap">
            % return from period start · All times US Eastern
          </span>
        </div>
      )}

      {/* ── Single-symbol header ── */}
      {!isMultiMode && (symbol || timeframe) && (
        <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-700 bg-slate-900/80">
          {symbol && (
            <span className="text-white font-semibold text-sm tracking-wide">{symbol}</span>
          )}
          {timeframe && (
            <span className="text-xs text-slate-400 bg-slate-800 px-2 py-0.5 rounded">{timeframe}</span>
          )}
          {currentPrice != null && (
            <span className="text-xs font-mono font-semibold text-blue-300 bg-blue-500/10 border border-blue-500/20 px-2 py-0.5 rounded">
              ${currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}
            </span>
          )}
          {/* Market hours badge */}
          <span
            title={marketStatus.label}
            className={`text-xs font-semibold px-2 py-0.5 rounded border ${
              marketStatus.open
                ? 'bg-green-500/15 text-green-400 border-green-500/30'
                : marketType === 'crypto'
                  ? 'bg-green-500/15 text-green-400 border-green-500/30'
                  : 'bg-slate-700 text-slate-400 border-slate-600'
            }`}
          >
            {marketStatus.badge}
          </span>
          {marketType === 'equities' && (
            <span className="text-xs text-slate-500 hidden sm:inline" title="NYSE regular session">
              9:30–16:00 ET
            </span>
          )}
          <div className="ml-auto flex items-center gap-3 text-xs overflow-hidden">
            {agentLegend && agentLegend.length > 0 ? (
              <>
                {agentLegend.map(a => (
                  <span key={a.id} className="flex items-center gap-1.5 whitespace-nowrap" style={{ color: a.color }}>
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: a.color }} />
                    {a.name}
                  </span>
                ))}
                <span className="text-slate-500 whitespace-nowrap">↑buy ↓sell</span>
              </>
            ) : (
              <>
                <span className="flex items-center gap-1 text-green-400">
                  <span className="w-3 h-0.5 bg-green-400 inline-block" /> BUY
                </span>
                <span className="flex items-center gap-1 text-red-400">
                  <span className="w-3 h-0.5 bg-red-400 inline-block" /> SELL
                </span>
              </>
            )}
            <span className="flex items-center gap-1 text-slate-400">
              <span className="w-3 h-0.5 bg-slate-600 inline-block" /> Volume
            </span>
          </div>
        </div>
      )}

      {/* Chart container */}
      <div ref={containerRef} style={{ height }} />

      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900/70 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-400 text-sm">Loading chart data…</span>
          </div>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && isEmpty && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-slate-500">
            <div className="text-4xl mb-2">📊</div>
            <div className="text-sm">No chart data available</div>
            <div className="text-xs mt-1 text-slate-600">
              {isMultiMode
                ? 'Enter comma-delimited symbols, e.g. BTC/USDT, ETH/USDT'
                : 'Select a symbol and exchange to load data'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(TradingChart);
